import React, { useEffect, useState } from "react";
import PieChartCard from "views/admin/default/components/PieChartCard";
import { MdBarChart, MdDashboard, MdMovie, MdTv, MdWarning } from "react-icons/md";
import { FiUsers, FiDownload } from "react-icons/fi";

import Widget from "components/widget/Widget";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import Card from "components/card";
import CardMenu from "components/card/CardMenu";

import api from "services/api";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await api.getAnalyticsOverview();
        setData(result);
      } catch (error) {
        console.error("Failed to fetch analytics", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!data) return <div>Failed to load dashboard</div>;

  return (
    <div>
      {/* Card widget */}
      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
        <Widget
          icon={<MdMovie className="h-7 w-7" />}
          title={"Total Movies"}
          subtitle={data.stats.movies.total.toString()}
        />
        <Widget
          icon={<MdTv className="h-6 w-6" />}
          title={"Total Series"}
          subtitle={data.stats.series.total.toString()}
        />
        <Widget
          icon={<FiUsers className="h-7 w-7" />}
          title={"Registered Users"}
          subtitle={data.stats.users.total.toString()}
        />
        <Widget
          icon={<FiUsers className="h-6 w-6" />}
          title={"Total Visitors"}
          subtitle={data.stats.visitors.total.toString()}
        />
        <Widget
          icon={<FiDownload className="h-7 w-7" />}
          title={"Downloads"}
          subtitle={data.stats.downloads.total.toString()}
        />
        <Widget
          icon={<MdWarning className="h-6 w-6" />}
          title={"Needs Attention"}
          subtitle={data.stats.needsAttention.total.toString()}
        />
      </div>

      {/* Traffic chart & Pie Chart */}
      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <DailyTraffic visits={data.visitsChart} total={data.stats.visitors.total} />
        <PieChartCard movies={data.stats.movies.total} series={data.stats.series.total} animation={data.stats.animation.total} />
      </div>

      {/* Tables & Charts */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        {/* Needs Attention Table */}
        <Card extra={"w-full h-full sm:overflow-auto px-6"}>
          <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Needs Attention
            </div>
            <CardMenu />
          </div>

          <div className="mt-8 overflow-x-scroll xl:overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="!border-px !border-gray-400">
                  <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                    <p className="text-sm font-bold text-gray-600 dark:text-white">TASK</p>
                  </th>
                  <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                    <p className="text-sm font-bold text-gray-600 dark:text-white">COUNT</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-navy-700 transition">
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-navy-700 dark:text-white">Missing posters</p>
                  </td>
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-brand-500 dark:text-white">{data.attentionRows.missingPosters}</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-navy-700 transition">
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-navy-700 dark:text-white">Movies without video</p>
                  </td>
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-brand-500 dark:text-white">{data.attentionRows.missingMoviesVideo}</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-navy-700 transition">
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-navy-700 dark:text-white">Series without seasons</p>
                  </td>
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-brand-500 dark:text-white">{data.attentionRows.missingSeriesSeasons}</p>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-navy-700 transition">
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-navy-700 dark:text-white">Seasons without episodes</p>
                  </td>
                  <td className="py-4 pr-4">
                    <p className="text-base font-bold text-brand-500 dark:text-white">{data.attentionRows.missingSeasonsEpisodes}</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* Trending List (HistoryCard style) */}
        <Card extra={"w-full h-full !z-5 overflow-hidden"}>
          <div className="flex items-center justify-between rounded-t-3xl p-6">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Popular Content
            </div>
            <button className="linear rounded-[20px] bg-lightPrimary px-4 py-2 text-base font-medium text-brand-500 transition duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20">
              See all
            </button>
          </div>

          <div className="flex flex-col pb-4">
            {data.trending.length === 0 && (
              <div className="px-6 py-4 text-center text-gray-500">No downloads yet</div>
            )}
            {data.trending.map((t, index) => (
              <div key={index} className="flex h-full w-full items-start justify-between bg-white px-6 py-[20px] hover:shadow-2xl dark:!bg-navy-800 dark:shadow-none dark:hover:!bg-navy-700">
                <div className="flex items-center gap-3">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-lightPrimary dark:bg-navy-700">
                    {t.title?.posterKey ? (
                      <img
                        className="h-full w-full rounded-xl object-cover"
                        src={`https://pub-26704679c67f47a28bce53f5dac3f586.r2.dev/${t.title.posterKey}`}
                        alt={t.title.title}
                      />
                    ) : (
                      <MdMovie className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <h5 className="text-base font-bold text-navy-700 dark:text-white line-clamp-1">
                      {t.title?.title || "Unknown Title"}
                    </h5>
                    <p className="mt-1 text-sm font-normal text-gray-600 line-clamp-1">
                      {t.title?.year ? `Year ${t.title.year}` : "Movie"}
                    </p>
                  </div>
                </div>

                <div className="mt-1 flex items-center justify-center text-navy-700 dark:text-white">
                  <div>
                    <FiDownload className="text-brand-500" />
                  </div>
                  <div className="ml-1 flex items-center text-sm font-bold text-navy-700 dark:text-white">
                    <p className="mr-1">{t.count}</p> <p>Downloads</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
